<script setup lang="ts">
import StatsGraph from '../components/global/StatsGraph.vue';
import { onMounted, ref} from "vue";
import { useOrderStore } from '../store/orders';
import {useAuthStore} from "../store/auth";

const authStore = useAuthStore()
const date = ref(new Date())
const store = useOrderStore()
const is_loading = ref(false)
const getData = async () => {
    is_loading.value = true
    await store.getStats(date.value)
    await authStore.getHomeStats()
    is_loading.value = false
}
const time = ref(date.value.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }))
setInterval(() => {
    time.value = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}, 1000)
onMounted(async () => {
    await getData()
})
</script>

<template>
    <main class="home-page">
        <div class="home-page-overview">
            <div class="home-page-overview-date">
                <p>{{date.toLocaleString('en-GB', {weekday: 'long'})}}</p>
                <p>{{date.toLocaleString('en-GB', {day: 'numeric', month: "short", year: "numeric"})}}</p>
                <p>{{time}}</p>
            </div>

            <div class="home-page-overview-stats">
                <div class="home-page-overview-stats-badge">
                    <div class="home-page-overview-stats-badge-content">
                        <div class="badge-title">
                            <div class="badge-title-icon">
                                <ah-icon name="speechbubble-double" />
                            </div>

                            <p>Open tickets.</p>
                        </div>
                        <p class="badge-details">
                            {{authStore.home_stats?.tickets}}
                        </p>
                    </div>
                </div>
                <div class="home-page-overview-stats-badge">
                    <div class="home-page-overview-stats-badge-content">
                        <div class="badge-title">
                            <div class="badge-title-icon">
                                <ah-icon name="call-default" />
                            </div>

                            <p>Upcoming calls.</p>
                        </div>
                        <p class="badge-details">
                            {{authStore.home_stats?.calls?.all}}
                        </p>
                    </div>
                    <div class="home-page-overview-stats-badge-stats">
                        <div class="home-page-overview-stats-badge-stats-block">
                            <p>New clients.</p>
                            <p>{{authStore.home_stats?.calls?.new}}</p>
                        </div>
                        <div class="home-page-overview-stats-badge-stats-block">
                            <p>Current clients.</p>
                            <p>{{authStore.home_stats?.calls?.old}}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="home-page-graph">
            <p class="home-page-graph-heading">
                Order stats.
            </p>

            <StatsGraph
                v-if="store.stats && !is_loading"
                :period="store?.stats?.period"
                :chart_data="store?.stats?.chart_data"
                :labels="store?.stats?.labels"
                :stats="store?.stats?.stats"
            />
        </div>
    </main>
</template>

<style lang="scss">
.home-page {
    display: flex;
    gap: 4rem;
    padding: 2rem 4rem;
    align-items: flex-end;

    &-overview {
        display: flex;
        flex-direction: column;
        gap: 7rem;
        width: 47rem;

        &-date {
            p {
                text-align: center;
                @include font(4.461,4.461, book);

                &:nth-child(2) {
                    @include font(2.974,2.974,black);
                }

                &:last-child {
                    @include font(14.9,14.9,book);
                    color: var(--color-tiffany);
                    margin-top: 5rem;
                }
            }
        }

        &-stats {
            display: flex;
            flex-direction: column;
            gap: 2rem;

            &-badge {
                min-height: 10rem;
                background: var(--color-white);
                border-radius: 5rem;

                &-content {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 3.2rem 1rem 1rem;

                    .badge-title {
                        display: flex;
                        gap: 2rem;
                        align-items: center;

                        .badge-title-icon {
                            width: 8rem;
                            height: 8rem;
                            background: var(--color-tiffany);
                            border-radius: 50%;
                            display: flex;
                            align-items: center;
                            justify-content: center;

                            .icon {
                                width: 3.6rem;
                                height: 3.6rem;
                                fill: var(--color-white);
                            }
                        }

                        p {
                            @include font(1.4, 1.4, black);
                        }
                    }

                    .badge-details {
                        @include font(4.8,4.8,book);
                    }
                }

                &-stats {
                    width: 100%;
                    height: 10rem;
                    border-top: .1rem solid var(--color-dew);
                    display: flex;

                    &-block {
                        flex: 1;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        padding: 0 2rem 0 3rem;

                        &:first-child {
                            border-right: .1rem solid var(--color-dew);
                        }

                        p {
                            @include font(1.4,1.4,black);

                            &:last-child {
                                @include font(3.6,3.6,book);
                            }
                        }
                    }
                }
            }
        }

        @include dark() {
            &-stats {
                &-badge {
                    background: var(--color-darth-maul);
                }
            }
        }
    }

    &-graph {
        width: calc(100% - 51rem);
        background: var(--color-white);
        border-radius: 6rem;
        padding: 2rem;
        display: flex;
        flex-direction: column;
        gap: 2rem;
        max-height: 100%;
        overflow-y: auto;

        &-heading {
            width: 100%;
            min-height: 8.4rem;
            @include font(2.4,2.4,black);
            background: var(--color-mist);
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 4.2rem;
        }

        @include dark() {
            background: var(--color-darth-maul);

            &-heading {
                background: var(--color-darth-vader);
            }
        }
    }

    @include mobile() {
        flex-direction: column;
        gap: 4rem;
        padding: 4rem 0 0;

        &-overview, &-graph {
            width: 100%;
        }

        &-overview {
            gap: 4rem;
            padding: 0 1rem;

            &-date {
                p {
                    @include font(3.6,3.6,book);

                    &:nth-child(2) {
                        @include font(2.4,2.4,black);
                    }

                    &:last-child {
                        @include font(10,10,book);
                        margin-top: 3rem;
                    }
                }
            }

            &-stats {
                &-badge {
                    &-content {
                        .badge-details {
                            @include font(4.6,4.6,book);
                        }
                    }
                    &-stats {
                        flex-direction: column;
                        border: none;
                        height: 16rem;

                        &-block {
                            border: solid var(--color-darth-vader);
                            border-width: .1rem 0 0 0;

                            &:first-child {
                                border-right: none;
                            }
                        }
                    }
                }
            }
        }

        &-graph {
            border-radius: unset;
            padding: 4rem 1rem;
        }
    }
}
</style>
